import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { theme } from '../theme';
import peuplesData from '../data/peuples.json';
import regionsCulturellesData from '../data/regionsCulturellesData.json';
import SectionModal from './SectionModal';

// Déclaration globale pour Google Maps
declare global {
  interface Window {
    google: any;
    initMap: () => void;
  }
}

const MapContainer = styled.div`
  width: 100%;
  height: 80vh;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
`;

// Fonction pour normaliser les noms (enlever accents, mettre en minuscule)
const normalizeName = (name: string): string => {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim();
};

// Fonction pour convertir les données de peuple en format sections pour le modal
const convertPeupleToSections = (peuple: any): { [key: string]: string[] | string } => {
  // Utiliser les sections du JSON si elles existent, sinon créer un objet vide
  const sections: { [key: string]: string[] | string } = peuple.sections ? { ...peuple.sections } : {};
  
  // Filtrer les sections vides ou undefined
  Object.keys(sections).forEach(key => {
    const value = sections[key];
    if (!value || (typeof value === 'string' && value.trim() === '') || (Array.isArray(value) && value.length === 0)) {
      delete sections[key];
    }
  });
  
  // Ajouter les sections "Présence" et "Régions culturelles" si elles n'existent pas déjà
  if (!sections['Présence'] && peuple.wilayas && peuple.wilayas.length > 0) {
    const wilayasList: string[] = [];
    peuple.wilayas.forEach((w: any) => {
      if (w.dairas && w.dairas.length > 0) {
        wilayasList.push(`${w.wilaya} (${w.dairas.join(', ')})`);
      } else {
        wilayasList.push(w.wilaya);
      }
    });
    sections['Présence'] = wilayasList;
  }
  
  if (!sections['Régions culturelles'] && peuple.regions && peuple.regions.length > 0) {
    const regionsMap = new Map<number, string>();
    regionsCulturellesData.regions.forEach((region: any) => {
      regionsMap.set(region.id, region.name);
    });
    
    sections['Régions culturelles'] = peuple.regions.map((rId: number) => {
      return regionsMap.get(rId) || `Région ${rId}`;
    });
  }
  
  return sections;
};

const PeuplesMap: React.FC = () => {
  const [selectedPeuple, setSelectedPeuple] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalWidth, setModalWidth] = useState<number | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [wilayasCenters, setWilayasCenters] = useState<{ [key: string]: { lat: number; lng: number } } | null>(null);
  const [dairasGeoData, setDairasGeoData] = useState<any>(null);
  const [algeriaBorders, setAlgeriaBorders] = useState<any>(null);
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const bordersLayerRef = useRef<any>(null);
  const googleMapsCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  
  // Créer un Map des peuples par ID pour lookup O(1)
  const peuplesMap = useMemo(() => {
    const map = new Map<number, any>();
    peuplesData.peuples.forEach(peuple => {
      map.set(peuple.id, peuple);
    });
    return map;
  }, []);

  const handleOpenModal = useCallback((peuple: any) => {
    setSelectedPeuple(peuple.id);
    
    // Précharger l'image si elle existe et calculer la largeur
    if (peuple.imageUrl) {
      const img = new Image();
      img.onload = () => {
        const maxHeight = 600;
        const aspectRatio = img.width / img.height;
        const calculatedWidth = Math.min(maxHeight * aspectRatio, 800);
        setModalWidth(calculatedWidth);
        setIsModalOpen(true);
      };
      img.onerror = () => {
        setModalWidth(500);
        setIsModalOpen(true);
      };
      img.src = peuple.imageUrl;
    } else {
      setModalWidth(500);
      setIsModalOpen(true);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setSelectedPeuple(null);
    setIsModalOpen(false);
    setModalWidth(null);
  }, []);

  // Charger les centres des wilayas (fichier léger)
  useEffect(() => {
    fetch('/data/wilayas_centers.json')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setWilayasCenters(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de wilayas_centers.json:', error);
      });
  }, []);

  // Charger les données GeoJSON des daïras pour calculer leurs centres
  useEffect(() => {
    fetch('/data/dairas_light1.geojson')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setDairasGeoData(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de dairas_light1.geojson:', error);
      });
  }, []);

  // Charger les frontières du pays (contour extérieur uniquement)
  useEffect(() => {
    fetch('/data/algeria_border.geojson')
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        setAlgeriaBorders(data);
      })
      .catch(error => {
        console.error('Erreur lors du chargement de algeria_border.geojson:', error);
      });
  }, []);

  // Charger Google Maps API dans un useEffect séparé
  useEffect(() => {
    if (!window.google && !document.querySelector('script[src*="maps.googleapis.com"]')) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyDgmbAXJ9fkmwRlYvg7ygBLsgJ6TP2bp1w&libraries=geometry`;
      script.async = true;
      script.defer = true;
      document.head.appendChild(script);
    }
  }, []);

  // Initialiser la carte quand Google Maps ET wilayasCenters sont disponibles
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    // Fonction pour initialiser la carte si toutes les conditions sont remplies
    const tryInitMap = () => {
      if (window.google && wilayasCenters && mapRef.current && !mapInstance.current) {
        if (googleMapsCheckIntervalRef.current) {
          clearInterval(googleMapsCheckIntervalRef.current);
          googleMapsCheckIntervalRef.current = null;
        }
        initMap();
      }
    };

    // Si Google Maps est déjà chargé, initialiser immédiatement
    tryInitMap();

    // Vérifier périodiquement si Google Maps est chargé (pour gérer le cas où le script se charge après)
    if (!window.google) {
      googleMapsCheckIntervalRef.current = setInterval(tryInitMap, 100);
    }

    function initMap() {
      if (mapRef.current && !mapInstance.current && wilayasCenters && window.google) {
        // Initialiser la carte
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: { lat: 28.0339, lng: 1.6596 },
          zoom: 5,
          mapTypeId: 'satellite',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: false,
          scaleControl: false,
          rotateControl: false,
          panControl: false,
          overviewMapControl: false,
          scrollwheel: true,
          disableDoubleClickZoom: false,
          keyboardShortcuts: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
          styles: [
            {
              featureType: 'all',
              elementType: 'labels',
              stylers: [{ visibility: 'off' }]
            }
          ]
        });

        // Nettoyer les marqueurs existants
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];

        // Ajouter les frontières si elles sont déjà chargées
        if (algeriaBorders) {
          try {
            const fixedBorders = JSON.parse(JSON.stringify(algeriaBorders));
            if (fixedBorders.features) {
              fixedBorders.features.forEach((feature: any) => {
                if (feature.geometry && feature.geometry.coordinates) {
                  if (feature.geometry.type === 'Polygon') {
                    feature.geometry.coordinates.forEach((ring: any[]) => {
                      if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                        ring.push([...ring[0]]);
                      }
                    });
                  } else if (feature.geometry.type === 'MultiPolygon') {
                    feature.geometry.coordinates.forEach((polygon: any[]) => {
                      polygon.forEach((ring: any[]) => {
                        if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                          ring.push([...ring[0]]);
                        }
                      });
                    });
                  }
                }
              });
            }

            if (bordersLayerRef.current) {
              bordersLayerRef.current.setMap(null);
            }

            bordersLayerRef.current = new window.google.maps.Data();
            bordersLayerRef.current.setMap(mapInstance.current);
            
            bordersLayerRef.current.addGeoJson(fixedBorders);
            
            bordersLayerRef.current.setStyle({
              fillColor: '#808080',
              fillOpacity: 0.3,
              strokeColor: 'transparent',
              strokeWeight: 0,
              strokeOpacity: 0
            });
          } catch (error) {
            console.error('Erreur lors de l\'ajout des frontières:', error);
          }
        }

        // Fonction pour créer les marqueurs
        const createMarkers = () => {
          if (!mapInstance.current || !wilayasCenters) return;
          
          // Nettoyer les marqueurs existants
          markersRef.current.forEach(marker => marker.setMap(null));
          markersRef.current = [];

          // Fonction pour calculer le centre d'une daïra depuis le GeoJSON
          const getDairaCenter = (dairaName: string, wilayaName: string): { lat: number; lng: number } | null => {
            if (!dairasGeoData || !dairasGeoData.features) return null;
            
            const normalizedDairaName = normalizeName(dairaName);
            
            for (const feature of dairasGeoData.features) {
              const props = feature.properties;
              // Le fichier dairas_light1.geojson utilise name:fr pour le nom de la daïra
              const featureDairaName = props?.['name:fr'] || props?.name || '';
              
              const normalizedFeatureDaira = normalizeName(featureDairaName);
              
              // Comparer uniquement le nom de la daïra (sans préfixe "Daïra ")
              const cleanDairaName = normalizedDairaName.replace(/^daira\s+/i, '');
              const cleanFeatureDaira = normalizedFeatureDaira.replace(/^daira\s+/i, '');
              
              if (cleanFeatureDaira === cleanDairaName || normalizedFeatureDaira.includes(normalizedDairaName) || normalizedDairaName.includes(normalizedFeatureDaira)) {
                // Calculer le centre du polygone
                if (feature.geometry && feature.geometry.coordinates) {
                  let totalLat = 0;
                  let totalLng = 0;
                  let count = 0;
                  
                  const processCoordinates = (coords: any[]) => {
                    if (Array.isArray(coords[0])) {
                      coords.forEach(coord => processCoordinates(coord));
                    } else if (coords.length >= 2) {
                      totalLng += coords[0];
                      totalLat += coords[1];
                      count++;
                    }
                  };
                  
                  processCoordinates(feature.geometry.coordinates);
                  
                  if (count > 0) {
                    return { lat: totalLat / count, lng: totalLng / count };
                  }
                }
              }
            }
            return null;
          };

          // Compter combien de peuples sont dans chaque wilaya
          const wilayasPeuplesCount: { [key: string]: number } = {};
          peuplesData.peuples.forEach(peuple => {
            peuple.wilayas.forEach((w: any) => {
              wilayasPeuplesCount[w.wilaya] = (wilayasPeuplesCount[w.wilaya] || 0) + 1;
            });
          });

          // Pour chaque peuple, créer des marqueurs pour chaque wilaya où il est présent
          requestAnimationFrame(() => {
          let markersCreated = 0;
          let totalWilayasExpected = 0;
          peuplesData.peuples.forEach(peuple => {
            totalWilayasExpected += peuple.wilayas.length;
            peuple.wilayas.forEach((wilayaData: any) => {
              const wilayaName = wilayaData.wilaya;
              const normalizedWilayaName = normalizeName(wilayaName);
              const hasMultiplePeuples = wilayasPeuplesCount[wilayaName] > 1;
              const hasDairas = wilayaData.dairas && wilayaData.dairas.length > 0;
              
              // Si plusieurs peuples dans la même wilaya ET que le peuple a des daïras ET que les données GeoJSON sont chargées, utiliser les daïras
              const useDairas = hasMultiplePeuples && hasDairas && dairasGeoData;
              
              let centers: { lat: number; lng: number }[] = [];
              
              if (useDairas) {
                // Utiliser les centres des daïras
                wilayaData.dairas.forEach((dairaName: string) => {
                  const dairaCenter = getDairaCenter(dairaName, wilayaName);
                  if (dairaCenter) {
                    centers.push(dairaCenter);
                  }
                });
                
                // Si aucune daïra trouvée, utiliser le centre de la wilaya
                if (centers.length === 0) {
                  const wilayaCenter = wilayasCenters?.[wilayaName] || wilayasCenters?.[normalizedWilayaName];
                  if (wilayaCenter) {
                    centers.push(wilayaCenter);
                  }
                }
              } else {
                // Utiliser le centre de la wilaya
                let center: { lat: number; lng: number } | undefined = undefined;
                if (wilayasCenters) {
                  center = wilayasCenters[wilayaName] || wilayasCenters[normalizedWilayaName];
                  
                  // Si pas trouvé, essayer avec différentes variations du nom
                  if (!center) {
                    for (const [key, value] of Object.entries(wilayasCenters)) {
                      const normalizedKey = normalizeName(key);
                      if (normalizedKey === normalizedWilayaName || 
                          key.toLowerCase().includes(wilayaName.toLowerCase()) ||
                          wilayaName.toLowerCase().includes(key.toLowerCase())) {
                        center = value as { lat: number; lng: number };
                        break;
                      }
                    }
                  }
                }
                if (center) {
                  centers.push(center);
                }
              }
              
              // Créer un marqueur pour chaque centre trouvé
              centers.forEach((center, index) => {
                const isDairaMarker = useDairas && centers.length > 1;
                const markerScale = isDairaMarker ? 6 : 8; // Plus petit pour les daïras
                
                const svgIcon = {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: markerScale,
                  fillColor: peuple.color,
                  fillOpacity: 1,
                  strokeColor: 'transparent',
                  strokeWeight: 0,
                  anchor: new window.google.maps.Point(0, 0)
                };

                const marker = new window.google.maps.Marker({
                  position: center,
                  map: mapInstance.current,
                  title: `${peuple.name} - ${wilayaName}${isDairaMarker ? ` (${wilayaData.dairas[index]})` : ''}`,
                  icon: svgIcon,
                  zIndex: 1000,
                  optimized: false,
                  visible: true
                });

                // Ajouter un listener de clic pour ouvrir le modal
                marker.addListener('click', () => {
                  handleOpenModal(peuple);
                });

                // Ajouter un effet hover pour améliorer l'interactivité
                marker.addListener('mouseover', () => {
                  marker.setIcon({
                    ...svgIcon,
                    scale: markerScale + 2
                  });
                });

                marker.addListener('mouseout', () => {
                  marker.setIcon(svgIcon);
                });

                markersRef.current.push(marker);
                markersCreated++;
              });
            });
          });
          });
        };
        
        // Créer les marqueurs
        createMarkers();
      }
    }
    
    // Recréer les marqueurs si la carte est déjà initialisée et que dairasGeoData vient d'être chargé
    if (mapInstance.current && wilayasCenters && dairasGeoData) {
      // Nettoyer les marqueurs existants
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      
      // Réutiliser la logique de création des marqueurs depuis initMap
      // On doit recréer les marqueurs avec les nouvelles données de daïras
      const createMarkers = () => {
        if (!mapInstance.current || !wilayasCenters) return;
        
        // Fonction pour calculer le centre d'une daïra depuis le GeoJSON
        const getDairaCenter = (dairaName: string, wilayaName: string): { lat: number; lng: number } | null => {
          if (!dairasGeoData || !dairasGeoData.features) return null;
          
          const normalizedDairaName = normalizeName(dairaName);
          
          for (const feature of dairasGeoData.features) {
            const props = feature.properties;
            const featureDairaName = props?.['name:fr'] || props?.name || '';
            const normalizedFeatureDaira = normalizeName(featureDairaName);
            const cleanDairaName = normalizedDairaName.replace(/^daira\s+/i, '');
            const cleanFeatureDaira = normalizedFeatureDaira.replace(/^daira\s+/i, '');
            
            if (cleanFeatureDaira === cleanDairaName || normalizedFeatureDaira.includes(normalizedDairaName) || normalizedDairaName.includes(normalizedFeatureDaira)) {
              if (feature.geometry && feature.geometry.coordinates) {
                let totalLat = 0;
                let totalLng = 0;
                let count = 0;
                
                const processCoordinates = (coords: any[]) => {
                  if (Array.isArray(coords[0])) {
                    coords.forEach(coord => processCoordinates(coord));
                  } else if (coords.length >= 2) {
                    totalLng += coords[0];
                    totalLat += coords[1];
                    count++;
                  }
                };
                
                processCoordinates(feature.geometry.coordinates);
                
                if (count > 0) {
                  return { lat: totalLat / count, lng: totalLng / count };
                }
              }
            }
          }
          return null;
        };

        const wilayasPeuplesCount: { [key: string]: number } = {};
        peuplesData.peuples.forEach(peuple => {
          peuple.wilayas.forEach((w: any) => {
            wilayasPeuplesCount[w.wilaya] = (wilayasPeuplesCount[w.wilaya] || 0) + 1;
          });
        });

        requestAnimationFrame(() => {
          let markersCreated = 0;
          peuplesData.peuples.forEach(peuple => {
            peuple.wilayas.forEach((wilayaData: any) => {
              const wilayaName = wilayaData.wilaya;
              const normalizedWilayaName = normalizeName(wilayaName);
              const hasMultiplePeuples = wilayasPeuplesCount[wilayaName] > 1;
              const hasDairas = wilayaData.dairas && wilayaData.dairas.length > 0;
              const useDairas = hasMultiplePeuples && hasDairas && dairasGeoData;
              
              let centers: { lat: number; lng: number }[] = [];
              
              if (useDairas) {
                wilayaData.dairas.forEach((dairaName: string) => {
                  const dairaCenter = getDairaCenter(dairaName, wilayaName);
                  if (dairaCenter) {
                    centers.push(dairaCenter);
                  }
                });
                
                if (centers.length === 0) {
                  const wilayaCenter = wilayasCenters?.[wilayaName] || wilayasCenters?.[normalizedWilayaName];
                  if (wilayaCenter) {
                    centers.push(wilayaCenter);
                  }
                }
              } else {
                let center: { lat: number; lng: number } | undefined = undefined;
                if (wilayasCenters) {
                  center = wilayasCenters[wilayaName] || wilayasCenters[normalizedWilayaName];
                  
                  if (!center) {
                    for (const [key, value] of Object.entries(wilayasCenters)) {
                      const normalizedKey = normalizeName(key);
                      if (normalizedKey === normalizedWilayaName || 
                          key.toLowerCase().includes(wilayaName.toLowerCase()) ||
                          wilayaName.toLowerCase().includes(key.toLowerCase())) {
                        center = value as { lat: number; lng: number };
                        break;
                      }
                    }
                  }
                }
                if (center) {
                  centers.push(center);
                }
              }
              
              centers.forEach((center, index) => {
                const isDairaMarker = useDairas && centers.length > 1;
                const markerScale = isDairaMarker ? 6 : 8;
                
                const svgIcon = {
                  path: window.google.maps.SymbolPath.CIRCLE,
                  scale: markerScale,
                  fillColor: peuple.color,
                  fillOpacity: 1,
                  strokeColor: 'transparent',
                  strokeWeight: 0,
                  anchor: new window.google.maps.Point(0, 0)
                };

                const marker = new window.google.maps.Marker({
                  position: center,
                  map: mapInstance.current,
                  title: `${peuple.name} - ${wilayaName}${isDairaMarker ? ` (${wilayaData.dairas[index]})` : ''}`,
                  icon: svgIcon,
                  zIndex: 1000,
                  optimized: false,
                  visible: true
                });

                marker.addListener('click', () => {
                  handleOpenModal(peuple);
                });

                marker.addListener('mouseover', () => {
                  marker.setIcon({
                    ...svgIcon,
                    scale: markerScale + 2
                  });
                });

                marker.addListener('mouseout', () => {
                  marker.setIcon(svgIcon);
                });

                markersRef.current.push(marker);
                markersCreated++;
              });
            });
          });
        });
      };
      
      createMarkers();
    }
    
    return () => {
      window.removeEventListener('resize', checkMobile);
      // Nettoyer l'intervalle de vérification de Google Maps
      if (googleMapsCheckIntervalRef.current) {
        clearInterval(googleMapsCheckIntervalRef.current);
        googleMapsCheckIntervalRef.current = null;
      }
      // Nettoyer proprement
      if (mapInstance.current) {
        markersRef.current.forEach(marker => marker.setMap(null));
        markersRef.current = [];
        if (bordersLayerRef.current) {
          bordersLayerRef.current.setMap(null);
        }
        mapInstance.current = null;
      }
    };
  }, [wilayasCenters, dairasGeoData, algeriaBorders, handleOpenModal]);
  
  // Ajouter les frontières quand elles sont chargées ET que la carte est initialisée
  useEffect(() => {
    if (mapInstance.current && algeriaBorders) {
      try {
        const fixedBorders = JSON.parse(JSON.stringify(algeriaBorders));
        if (fixedBorders.features) {
          fixedBorders.features.forEach((feature: any) => {
            if (feature.geometry && feature.geometry.coordinates) {
              if (feature.geometry.type === 'Polygon') {
                feature.geometry.coordinates.forEach((ring: any[]) => {
                  if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                    ring.push([...ring[0]]);
                  }
                });
              } else if (feature.geometry.type === 'MultiPolygon') {
                feature.geometry.coordinates.forEach((polygon: any[]) => {
                  polygon.forEach((ring: any[]) => {
                    if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
                      ring.push([...ring[0]]);
                    }
                  });
                });
              }
            }
          });
        }

        if (bordersLayerRef.current) {
          bordersLayerRef.current.setMap(null);
        }

        bordersLayerRef.current = new window.google.maps.Data();
        bordersLayerRef.current.setMap(mapInstance.current);
        
        bordersLayerRef.current.addGeoJson(fixedBorders);
        
        bordersLayerRef.current.setStyle({
          fillColor: '#808080',
          fillOpacity: 0.3,
          strokeColor: 'transparent',
          strokeWeight: 0,
          strokeOpacity: 0
        });
      } catch (error) {
        console.error('Erreur lors de l\'ajout des frontières:', error);
      }
    }
  }, [algeriaBorders, wilayasCenters]); // Ajouter wilayasCenters pour se déclencher quand la carte est initialisée

  const toggleFullscreen = async () => {
    try {
      if (!isFullscreen) {
        if (mapRef.current?.requestFullscreen) {
          await mapRef.current.requestFullscreen();
        }
      } else {
        if (document.exitFullscreen) {
          await document.exitFullscreen();
        } else if ((document as any).webkitExitFullscreen) {
          await (document as any).webkitExitFullscreen();
        } else if ((document as any).mozCancelFullScreen) {
          await (document as any).mozCancelFullScreen();
        } else if ((document as any).msExitFullscreen) {
          await (document as any).msExitFullscreen();
        }
      }
    } catch (error) {
      console.error('Erreur plein écran:', error);
    }
  };

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenActive = !!(
        document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement ||
        (document as any).msFullscreenElement
      );
      setIsFullscreen(isFullscreenActive);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', handleFullscreenChange);
    document.addEventListener('mozfullscreenchange', handleFullscreenChange);
    document.addEventListener('MSFullscreenChange', handleFullscreenChange);

    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', handleFullscreenChange);
      document.removeEventListener('mozfullscreenchange', handleFullscreenChange);
      document.removeEventListener('MSFullscreenChange', handleFullscreenChange);
    };
  }, []);

  const getCurrentPeuple = () => {
    if (!selectedPeuple) return null;
    return peuplesMap.get(selectedPeuple) || null;
  };

  const currentPeuple = getCurrentPeuple();

  return (
    <MapContainer className="map-container" key="peuples-map">
      <button
        onClick={toggleFullscreen}
        style={{
          position: 'absolute',
          top: '10px',
          right: '10px',
          zIndex: 1000,
          background: 'rgba(255, 255, 255, 0.95)',
          border: '2px solid rgba(0, 0, 0, 0.1)',
          borderRadius: '8px',
          padding: '0',
          cursor: 'pointer',
          fontSize: '16px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'all 0.2s ease',
          backdropFilter: 'blur(8px)',
          fontWeight: 'bold',
          color: '#333'
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 1)';
          e.currentTarget.style.transform = 'scale(1.05)';
          e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.2)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = 'rgba(255, 255, 255, 0.95)';
          e.currentTarget.style.transform = 'scale(1)';
          e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
        }}
        title={isFullscreen ? 'Sortir du plein écran' : 'Plein écran'}
      >
        {isFullscreen ? '⤢' : '⤡'}
      </button>

      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {currentPeuple && (
        <SectionModal
          title={currentPeuple.name}
          subtitle=""
          imageUrl={currentPeuple.imageUrl || ""}
          imageCaption={undefined}
          sections={convertPeupleToSections(currentPeuple)}
          color={currentPeuple.color}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          modalWidth={modalWidth}
        />
      )}
    </MapContainer>
  );
};

export default PeuplesMap;
